import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, RefreshCw, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tag "faca isso" que o Compras manda junto com o aviso (cancelar /
// ja_tenho_similar / data_faturamento). E sugestao: a atendente pode trocar.
const ROTULO_ORIENTACAO = {
  cancelar: 'Cancelar',
  ja_tenho_similar: 'Já tenho similar',
  data_faturamento: 'Data de faturamento',
};

const fmtData = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Data só (sem hora) — usada na coluna de tratativa
const fmtDataCurta = (iso) => {
  if (!iso) return '';
  try {
    // 'YYYY-MM-DD' puro: monta sem fuso pra não voltar 1 dia
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
};

// Badge da coluna "Tratativa" (status do ciclo do cancelamento/pedido)
const StatusTratativa = ({ status, label, data, temcasa }) => {
  const dataFmt = fmtDataCurta(data);
  const cores = {
    em_tratativa: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    cancelado: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300',
    faturado: 'bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300',
    similar: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
    // Tem em Casa: o aviso NÃO desce pro AES enquanto a outra tela resolve.
    tem_em_casa: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
    sem_cancelamento: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  };
  const cls = cores[status] || cores.sem_cancelamento;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className={`inline-flex items-center w-fit gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${cls}`}>
        {label || 'Sem tratativa'}
      </span>
      {/* O selo aparece mesmo quando o status é outro (ex.: AES aberto de antes
          desta regra): é o aviso de que as duas telas tratam a mesma entrega. */}
      {temcasa && (
        <a
          href={temcasa.url}
          target="_blank"
          rel="noreferrer"
          title={`TEM EM CASA — ${temcasa.tipo_label} · ${temcasa.status_label}`
            + `${temcasa.casou_por === 'entrega_nova' ? ' (casou pela entrega nova)' : ''}`
            + '. Enquanto estiver aqui, o aviso não desce pro AES. Clique para abrir.'}
          className="text-[10px] px-1.5 py-0 rounded-full font-semibold w-fit whitespace-nowrap
            bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200"
        >📦 Tem em Casa ↗</a>
      )}
      {dataFmt && <span className="text-[11px] text-muted-foreground">{dataFmt}</span>}
    </span>
  );
};

// Opções da coluna "Orientação (Compras)". Preenchível pela atendente nos avisos
// antigos sem a tag do Compras; nos novos vem sugerida e pode ser trocada.
const OPCOES_ORIENTACAO = [
  { v: '', label: '— definir —' },
  { v: 'cancelar', label: ROTULO_ORIENTACAO.cancelar },
  { v: 'ja_tenho_similar', label: ROTULO_ORIENTACAO.ja_tenho_similar },
  { v: 'data_faturamento', label: ROTULO_ORIENTACAO.data_faturamento },
];

// Célula editável: orientação (salva na hora, não fecha o aviso). Em "Já tenho
// similar" abre um campo de SKU e verifica se está cadastrado no catálogo.
const CelulaOrientacao = ({ aviso, saving, onSave, getAuthHeader, onAvisar, avisando }) => {
  const [orient, setOrient] = useState(aviso.orientacao || '');
  const [data, setData] = useState(aviso.orientacao_data || '');
  const [sku, setSku] = useState(aviso.orientacao_similar_sku || '');
  const [check, setCheck] = useState(null);
  useEffect(() => {
    setOrient(aviso.orientacao || '');
    setData(aviso.orientacao_data || '');
    setSku(aviso.orientacao_similar_sku || '');
  }, [aviso.orientacao, aviso.orientacao_data, aviso.orientacao_similar_sku]);

  const verificar = useCallback(async (valor) => {
    const t = (valor || '').trim();
    if (!t) { setCheck(null); return; }
    setCheck({ loading: true });
    try {
      const r = await axios.get(`${API_URL}/api/produtos/checar/${encodeURIComponent(t)}`, { headers: getAuthHeader() });
      setCheck(r.data || { existe: false });
    } catch {
      setCheck({ existe: false });
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (orient === 'ja_tenho_similar' && (aviso.orientacao_similar_sku || '').trim()) verificar(aviso.orientacao_similar_sku);
    else setCheck(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aviso.orientacao_similar_sku, orient]);

  const corSel = {
    cancelar: 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-300',
    ja_tenho_similar: 'border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300',
    data_faturamento: 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300',
  }[orient] || 'border-border text-muted-foreground';

  const escolher = (val) => {
    setOrient(val);
    if (val === 'data_faturamento') {
      if (data) onSave(aviso.id, { orientacao: val, orientacao_data: data });
    } else {
      onSave(aviso.id, { orientacao: val });
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <select
        value={orient}
        disabled={saving}
        onChange={(e) => escolher(e.target.value)}
        className={`text-xs font-semibold rounded-md border bg-background px-1.5 py-1 disabled:opacity-50 ${corSel}`}
        data-testid="orientacao-select"
      >
        {OPCOES_ORIENTACAO.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
      {orient === 'data_faturamento' && (
        <input
          type="date"
          value={data || ''}
          disabled={saving}
          onChange={(e) => { setData(e.target.value); if (e.target.value) onSave(aviso.id, { orientacao: 'data_faturamento', orientacao_data: e.target.value }); }}
          className="text-xs rounded-md border border-amber-300 dark:border-amber-700 bg-background px-1.5 py-0.5 disabled:opacity-50"
        />
      )}
      {orient === 'ja_tenho_similar' && (
        <div className="flex flex-col gap-0.5">
          <input
            type="text"
            value={sku}
            disabled={saving}
            placeholder="SKU do similar *"
            onChange={(e) => setSku(e.target.value.toUpperCase())}
            onBlur={(e) => { const v = e.target.value.trim().toUpperCase(); onSave(aviso.id, { orientacao_similar_sku: v }); verificar(v); }}
            className={`text-xs rounded-md border bg-background px-1.5 py-0.5 w-36 disabled:opacity-50 ${sku ? 'border-blue-300 dark:border-blue-700' : 'border-red-400 dark:border-red-600'}`}
            data-testid="similar-sku-input"
          />
          {!sku && <span className="text-[11px] text-red-500">SKU obrigatório</span>}
          {sku && check?.loading && <span className="text-[11px] text-muted-foreground">verificando…</span>}
          {sku && check && !check.loading && (check.existe
            ? <span className="text-[11px] text-emerald-700 dark:text-emerald-300 truncate max-w-[11rem]" title={check.descricao || ''}>✓ cadastrado{check.status ? ` · ${check.status}` : ''}</span>
            : (
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] text-red-600 dark:text-red-400">✗ não cadastrado</span>
                {aviso.avisado_cadastro_em
                  ? <span className="text-[11px] text-muted-foreground">✉️ cadastro avisado</span>
                  : <button type="button" disabled={avisando} onClick={() => onAvisar(aviso.id)} className="text-[11px] font-semibold text-primary hover:underline disabled:opacity-50 w-fit" data-testid="avisar-cadastro-btn">📧 Avisar cadastro</button>}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const AvisosCompras = () => {
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos'); // todos | naovem | faturou | encerrado
  const [filtroTratativa, setFiltroTratativa] = useState(''); // '' = todos; senão = status_tratativa (ou '__sem__')
  const [filtroOrientacao, setFiltroOrientacao] = useState(''); // '' = todos; senão = cancelar | ja_tenho_similar | data_faturamento
  const [buscaPedido, setBuscaPedido] = useState('');
  const [buscaProduto, setBuscaProduto] = useState('');
  const [dataDe, setDataDe] = useState('');
  const [dataAte, setDataAte] = useState('');
  const [salvandoId, setSalvandoId] = useState(null);
  const [avisandoId, setAvisandoId] = useState(null);
  const { getAuthHeader } = useAuth();

  const fetchAvisos = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/avisos-compras`, { headers: getAuthHeader() });
      setAvisos(r.data?.avisos || []);
    } catch {
      toast.error('Erro ao carregar avisos de compras');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchAvisos(); }, [fetchAvisos]);

  // Preenchimento manual da coluna "Orientação (Compras)". Grava só os campos
  // enviados (orientação, data ou SKU do similar); NÃO fecha o card.
  const salvarOrientacao = async (avisoId, campos) => {
    const payload = {};
    const patch = {};
    if ('orientacao' in campos) {
      const o = campos.orientacao || '';
      payload.orientacao = o;
      payload.orientacao_data = o === 'data_faturamento' ? (campos.orientacao_data || undefined) : undefined;
      patch.orientacao = o || null;
      patch.orientacao_data = o === 'data_faturamento' ? (campos.orientacao_data || null) : null;
      if (o !== 'ja_tenho_similar') patch.orientacao_similar_sku = null;
    }
    if ('orientacao_similar_sku' in campos) {
      payload.orientacao_similar_sku = campos.orientacao_similar_sku || '';
      patch.orientacao_similar_sku = campos.orientacao_similar_sku || null;
    }
    setSalvandoId(avisoId);
    try {
      await axios.put(`${API_URL}/api/avisos-compras/${avisoId}`, payload, { headers: getAuthHeader() });
      setAvisos(prev => prev.map(a => a.id === avisoId ? { ...a, ...patch } : a));
    } catch {
      toast.error('Erro ao salvar a orientação');
    } finally {
      setSalvandoId(null);
    }
  };

  // Envia o e-mail avisando que o similar do Compras não está cadastrado (disparo manual).
  const avisarCadastro = async (avisoId) => {
    if (!window.confirm('Enviar e-mail para Débora, Marcelo, Allan e Adnéia avisando que o similar indicado pelo Compras não está cadastrado?')) return;
    setAvisandoId(avisoId);
    try {
      await axios.post(`${API_URL}/api/avisos-compras/${avisoId}/avisar-cadastro`, {}, { headers: getAuthHeader() });
      setAvisos(prev => prev.map(a => a.id === avisoId ? { ...a, avisado_cadastro_em: new Date().toISOString() } : a));
      toast.success('E-mail enviado ao time de cadastro');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao enviar o e-mail');
    } finally {
      setAvisandoId(null);
    }
  };

  // Visão por situação da tratativa (não remove avisos — só filtra a exibição):
  //   naovem = ainda em tratativa/sem cancelamento | faturou = voltou a faturar | encerrado = cancelado/similar
  const visaoDe = (a) => {
    const st = a.status_tratativa;
    if (st === 'faturado' || a.status === 'faturado') return 'faturou';
    if (st === 'cancelado' || st === 'similar') return 'encerrado';
    return 'naovem';
  };
  // Opções da coluna Tratativa — derivadas dos dados reais (status_tratativa + label)
  const opcoesTratativa = useMemo(() => {
    const seen = new Map();
    avisos.forEach(a => {
      const key = a.status_tratativa || '__sem__';
      const label = a.status_tratativa_label || 'Sem tratativa';
      if (!seen.has(key)) seen.set(key, label);
    });
    return Array.from(seen.entries()).map(([key, label]) => ({ key, label }));
  }, [avisos]);

  // Ordem: precisa cadastro no topo · faturou depois sempre no final (reveja o atendimento)
  const rankOrdem = (a) => {
    if (a.status === 'faturado') return 2;
    if (a.precisa_cadastro) return 0;
    return 1;
  };
  const lista = avisos
    .filter(a => (filtro === 'todos' ? true : visaoDe(a) === filtro))
    .filter(a => (filtroTratativa ? (a.status_tratativa || '__sem__') === filtroTratativa : true))
    .filter(a => (filtroOrientacao ? (a.orientacao || '') === filtroOrientacao : true))
    .filter(a => {
      if (!buscaPedido.trim()) return true;
      const t = buscaPedido.trim().toLowerCase();
      return String(a.numero_pedido || '').toLowerCase().includes(t) || String(a.pedido_externo || '').toLowerCase().includes(t);
    })
    .filter(a => {
      if (!buscaProduto.trim()) return true;
      const t = buscaProduto.trim().toLowerCase();
      return String(a.produto || '').toLowerCase().includes(t) || String(a.sku || '').toLowerCase().includes(t);
    })
    .filter(a => {
      if (!dataDe && !dataAte) return true;
      const d = String(a.criado_em || '').slice(0, 10); // YYYY-MM-DD
      if (!d) return false;
      if (dataDe && d < dataDe) return false;
      if (dataAte && d > dataAte) return false;
      return true;
    })
    .sort((a, b) => rankOrdem(a) - rankOrdem(b));
  const countVisao = (v) => avisos.filter(a => visaoDe(a) === v).length;
  const nFaturados = avisos.filter(a => a.status === 'faturado').length;
  const fmtBRL = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const resumo = (st) => {
    const its = avisos.filter(a => a.status_tratativa === st);
    return { n: its.length, valor: its.reduce((s, a) => s + (a.valor_total || 0), 0) };
  };
  const rTratativa = resumo('em_tratativa');
  const rCancelado = resumo('cancelado');
  const rSimilar = resumo('similar');
  const rFaturado = resumo('faturado');
  const rErro = resumo('sem_cancelamento'); // aviso que NÃO migrou pro cancelamento (erro)

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-6 w-6 text-red-600" />
          <h1 className="text-xl font-bold">Avisos de Compras</h1>
          {nFaturados > 0 && <Badge className="bg-orange-500 hover:bg-orange-500 text-white">{nFaturados} faturou depois</Badge>}
        </div>
        <Button variant="outline" size="sm" onClick={fetchAvisos} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-3">
        Itens que o time de Compras marcou como "não vão chegar". A coluna <strong>Orientação (Compras)</strong> mostra o que o Compras indicou para o item
        (Cancelar, Já tenho similar ou Data de faturamento). Conforme o desfecho, o aviso muda de visão aqui: <strong>Não Vem</strong> (ainda em tratativa),
        <strong> Encerrado</strong> (cancelado ou recuperado com similar) e <strong>Faturou depois</strong> (voltou a ser faturado — reveja o atendimento).
      </p>

      {/* Resumo por status (quantidade + valor do pedido: produto + frete) — compacto, 1 linha só */}
      <div className="flex flex-nowrap items-stretch gap-1.5 mb-4 overflow-x-auto pb-0.5">
        {[
          { label: 'Em tratativa', n: rTratativa.n, valor: rTratativa.valor, cls: 'border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20', txt: 'text-amber-700 dark:text-amber-300', val: 'text-amber-800 dark:text-amber-200' },
          { label: 'Cancelado', n: rCancelado.n, valor: rCancelado.valor, cls: 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20', txt: 'text-red-700 dark:text-red-300', val: 'text-red-800 dark:text-red-200' },
          { label: 'Similar recuperado', n: rSimilar.n, valor: rSimilar.valor, cls: 'border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/20', txt: 'text-blue-700 dark:text-blue-300', val: 'text-blue-800 dark:text-blue-200' },
          ...(rFaturado.n > 0 ? [{ label: 'Faturou depois', n: rFaturado.n, valor: rFaturado.valor, cls: 'border-orange-200 dark:border-orange-900 bg-orange-50 dark:bg-orange-950/20', txt: 'text-orange-700 dark:text-orange-300', val: 'text-orange-800 dark:text-orange-200' }] : []),
          ...(rErro.n > 0 ? [{ label: 'Pendente (não migrou)', title: 'Aviso que não migrou para os Cancelamentos — verificar', n: rErro.n, valor: rErro.valor, cls: 'border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800', txt: 'text-slate-600 dark:text-slate-300', val: 'text-slate-700 dark:text-slate-200' }] : []),
        ].map((c, i) => (
          <div key={i} title={c.title} className={`flex items-center gap-2 shrink-0 whitespace-nowrap rounded-lg border px-3 py-1.5 ${c.cls}`}>
            <span className={`text-xs font-semibold uppercase tracking-wide ${c.txt}`}>{c.label}</span>
            <span className={`text-lg font-bold ${c.val}`}>{c.n}</span>
            <span className={`text-xs font-medium ${c.txt}`}>{fmtBRL(c.valor)}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-3">
        {[
          { k: 'todos', label: 'Todos' },
          { k: 'naovem', label: 'Não Vem' },
          { k: 'faturou', label: '⚠ Faturou depois' },
          { k: 'encerrado', label: 'Encerrado' },
        ].map(({ k, label }) => (
          <button
            key={k}
            onClick={() => setFiltro(k)}
            data-testid={`filtro-${k}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${
              filtro === k
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-border hover:bg-accent'
            }`}
          >
            {label}{k !== 'todos' ? ` (${countVisao(k)})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground py-10 text-center">Carregando…</div>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold align-top">
                  <div className="flex flex-col gap-1">
                    <span>Pedido (Entrega)</span>
                    <input
                      type="text"
                      value={buscaPedido}
                      onChange={(e) => setBuscaPedido(e.target.value)}
                      placeholder="Buscar…"
                      data-testid="busca-pedido-input"
                      className="text-[11px] font-normal rounded border border-border bg-background px-1.5 py-0.5 w-28"
                    />
                  </div>
                </th>
                <th className="px-3 py-2 font-semibold align-top">
                  <div className="flex flex-col gap-1">
                    <span>Produto</span>
                    <input
                      type="text"
                      value={buscaProduto}
                      onChange={(e) => setBuscaProduto(e.target.value)}
                      placeholder="Buscar…"
                      data-testid="busca-produto-input"
                      className="text-[11px] font-normal rounded border border-border bg-background px-1.5 py-0.5 w-32"
                    />
                  </div>
                </th>
                <th className="px-3 py-2 font-semibold whitespace-nowrap align-top">
                  <div className="flex flex-col gap-1">
                    <span>Orientação (Compras)</span>
                    <select
                      value={filtroOrientacao}
                      onChange={(e) => setFiltroOrientacao(e.target.value)}
                      title="Filtrar por orientação"
                      data-testid="filtro-orientacao-select"
                      className="text-[11px] font-normal normal-case rounded border border-border bg-background px-1.5 py-0.5"
                    >
                      <option value="">Todos</option>
                      {OPCOES_ORIENTACAO.filter(o => o.v).map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </div>
                </th>
                <th className="px-3 py-2 font-semibold whitespace-nowrap align-top">Estoque</th>
                <th className="px-3 py-2 font-semibold whitespace-nowrap align-top">
                  <div className="flex flex-col gap-1">
                    <span>Tratativa</span>
                    <select
                      value={filtroTratativa}
                      onChange={(e) => setFiltroTratativa(e.target.value)}
                      title="Filtrar por tratativa"
                      data-testid="filtro-tratativa-select"
                      className="text-[11px] font-normal normal-case rounded border border-border bg-background px-1.5 py-0.5 max-w-[8rem]"
                    >
                      <option value="">Todos</option>
                      {opcoesTratativa.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                  </div>
                </th>
                <th className="px-3 py-2 font-semibold align-top">Comentário</th>
                <th className="px-3 py-2 font-semibold whitespace-nowrap align-top">
                  <div className="flex flex-col gap-1">
                    <span>Quando</span>
                    <div className="flex flex-col gap-0.5">
                      <input
                        type="date"
                        value={dataDe}
                        onChange={(e) => setDataDe(e.target.value)}
                        title="De"
                        data-testid="filtro-data-de"
                        className="text-[11px] font-normal rounded border border-border bg-background px-1 py-0.5"
                      />
                      <input
                        type="date"
                        value={dataAte}
                        onChange={(e) => setDataAte(e.target.value)}
                        title="Até"
                        data-testid="filtro-data-ate"
                        className="text-[11px] font-normal rounded border border-border bg-background px-1 py-0.5"
                      />
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {lista.length === 0 && (
                <tr>
                  <td colSpan={99} className="px-3 py-10 text-center text-muted-foreground">
                    {(buscaPedido || buscaProduto)
                      ? 'Nenhum aviso encontrado para esta busca — os campos acima continuam disponíveis para consultar outro.'
                      : 'Nenhum aviso pendente. 🎉'}
                  </td>
                </tr>
              )}
              {lista.map(a => {
                const faturou = a.status === 'faturado';
                return (
                  <tr key={a.id}
                    className={`border-t border-border ${a.precisa_cadastro ? 'bg-amber-50 dark:bg-amber-950/30' : faturou ? 'bg-orange-50/50 dark:bg-orange-950/20' : ''}`}>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">
                      {a.numero_pedido}
                      {a.pedido_externo && <span className="text-muted-foreground"> · {a.pedido_externo}</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-medium">{a.produto || a.sku}</span>
                      {a.sku && <span className="text-muted-foreground font-mono text-xs"> · {a.sku}</span>}
                      {a.precisa_cadastro && (
                        <span className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100" title="Similar do Compras não cadastrado — acione o cadastro antes de seguir">
                          ⚠ precisa cadastro
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <CelulaOrientacao aviso={a} saving={salvandoId === a.id} onSave={salvarOrientacao} getAuthHeader={getAuthHeader} onAvisar={avisarCadastro} avisando={avisandoId === a.id} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      <div className="flex flex-col gap-0.5">
                        <span>XD: <b className={a.estoque_xd > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground'}>{a.estoque_xd ?? 0}</b></span>
                        {a.alerta_outra_filial && a.estoque_fisico > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-semibold"
                            title={`Estoque físico em ${(a.estoque_fisico_ufs || []).join(', ')} — entrega pela filial ${a.uf_filial_pedido || '?'}. Necessário subir pedido manual.`}
                          >
                            <AlertTriangle className="h-3 w-3" /> Físico: {a.estoque_fisico} ({(a.estoque_fisico_ufs || []).join(', ')})
                          </span>
                        ) : (
                          <span>Físico: <b className={a.estoque_fisico > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground'}>{a.estoque_fisico ?? 0}</b>
                            {a.estoque_fisico > 0 && a.estoque_fisico_ufs?.length > 0 && (
                              <span className="text-muted-foreground"> ({a.estoque_fisico_ufs.join(', ')})</span>
                            )}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <StatusTratativa
                        status={a.status_tratativa}
                        label={a.status_tratativa_label}
                        data={a.status_tratativa_data}
                        temcasa={a.temcasa}
                      />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground align-top" title={a.comentario || ''}>
                      <span className="block max-w-[26rem] whitespace-pre-wrap break-words">{a.comentario || '—'}</span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtData(a.criado_em)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AvisosCompras;
